// src/components/PricingStripe.jsx
import { useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import axios from "axios";
import { getAuth } from "firebase/auth";
import { useNavigate } from "react-router-dom";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const STRIPE_PUBLISHABLE = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

// stripe loader
const stripePromise = loadStripe(STRIPE_PUBLISHABLE);

// Pricing data adapted for Stripe (EUR)
const PLANS = [
  {
    name: "Free Plan",
    priceEUR: 0,
    features: [
      "Basic Resume Builder (1 template)",
      "Simple Cover Letter Generator (3/month)",
      "5 AI Interview Questions/month",
      "Standard Resume Review",
      "Email Support (48h response)",
      "Job Search Tips & Resources",
    ],
    recommended: false,
    cta: "Get Started",
  },
  {
    name: "Premium Plan",
    priceEUR: 5.0, // EUR price for Stripe flow
    features: [
      "Advanced Resume Builder (10+ templates)",
      "AI-Powered Cover Letters (Unlimited)",
      "Unlimited AI Interview Questions",
      "Live 1:1 Interview Coaching",
      "Priority 24/7 Support",
      "ATS-Optimized Resume & LinkedIn",
      "Personalized Job Matches",
      "Career Path Planning",
      "Salary Negotiation Guide",
      "Access to Exclusive Webinars",
    ],
    recommended: true,
    cta: "Subscribe Now",
  },
];

// ---------- Small Address form component (minimal, used only for collecting India export-required fields) ----------
function BillingAddressForm({ billing, setBilling }) {
  const update = (k, v) => setBilling((p) => ({ ...(p || {}), [k]: v }));

  return (
    <div className="space-y-2 p-4 bg-white/5 rounded-md text-sm text-white mb-4">
      <div className="font-semibold mb-2">Billing / Export details (required for EUR)</div>

      <div className="grid grid-cols-1 gap-2">
        <input
          value={billing?.name || ""}
          onChange={(e) => update("name", e.target.value)}
          placeholder="Full name"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.email || ""}
          onChange={(e) => update("email", e.target.value)}
          placeholder="Email (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.line1 || ""}
          onChange={(e) => update("line1", e.target.value)}
          placeholder="Address line 1"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <input
          value={billing?.line2 || ""}
          onChange={(e) => update("line2", e.target.value)}
          placeholder="Address line 2 (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
        <div className="grid grid-cols-2 gap-2">
          <input
            value={billing?.city || ""}
            onChange={(e) => update("city", e.target.value)}
            placeholder="City"
            className="p-2 rounded bg-white/5 text-black"
          />
          <input
            value={billing?.state || ""}
            onChange={(e) => update("state", e.target.value)}
            placeholder="State / Region"
            className="p-2 rounded bg-white/5 text-black"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            value={billing?.postal_code || ""}
            onChange={(e) => update("postal_code", e.target.value)}
            placeholder="Postal code"
            className="p-2 rounded bg-white/5 text-black"
          />
          <input
            value={billing?.country || ""}
            onChange={(e) => update("country", e.target.value.toUpperCase())}
            placeholder="Country (2-letter ISO, e.g. DE, FR)"
            className="p-2 rounded bg-white/5 text-black"
            maxLength={2}
          />
        </div>
        <input
          value={billing?.phone || ""}
          onChange={(e) => update("phone", e.target.value)}
          placeholder="Phone (optional)"
          className="w-full p-2 rounded bg-white/5 text-black"
        />
      </div>
    </div>
  );
}

// ---------- Inner payment form (PaymentElement) ----------
function StripeCheckoutForm({ clientSecret, amountEUR, onSuccess, onCancel }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // billing prefill from firebase user (optional)
  const auth = getAuth();
  const currentUser = auth.currentUser;
  const prefillName = currentUser?.displayName || "";
  const prefillEmail = currentUser?.email || "";

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError(null);

    if (!stripe || !elements) {
      setError("Stripe is not ready");
      return;
    }

    setLoading(true);
    try {
      // Confirm payment using Payment Element — we send return_url but we use redirect:'if_required'
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          // we remain on same page unless Stripe needs a redirect for SCA
          return_url: window.location.href,
        },
        redirect: "if_required",
      });

      if (result.error) {
        setError(result.error.message || "Payment failed");
        setLoading(false);
        return;
      }

      const pi = result.paymentIntent;
      if (!pi) {
        setError("No PaymentIntent returned");
        setLoading(false);
        return;
      }

      // Call server verify endpoint (source-of-truth)
      const freshUser = getAuth().currentUser;
      await axios.post(
        `${API_BASE_URL}/api/stripe/verify-payment`,
        {
          payment_intent_id: pi.id,
          uid: freshUser?.uid,
          plan: "premium"
        },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );

      onSuccess();
    } catch (err) {
      console.error("confirmPayment error", err);
      setError(err?.message || "Payment failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-md border p-4 bg-white text-black">
        <div className="mb-2 text-sm">
          Pay <strong>€{amountEUR.toFixed(2)}</strong> — one-time
        </div>

        <div className="mb-3">
          <PaymentElement />
        </div>

        {error && <div className="text-sm text-red-600 mb-2">{error}</div>}

        <div className="flex gap-2">
          <button
            type="submit"
            disabled={!stripe || loading}
            className="px-4 py-2 rounded-md bg-indigo-600 text-white disabled:opacity-60"
          >
            {loading ? "Processing…" : `Pay €${amountEUR.toFixed(2)}`}
          </button>

          <button
            type="button"
            onClick={onCancel}
            className="px-4 py-2 rounded-md border text-sm text-slate-700 bg-white"
            disabled={loading}
          >
            Cancel
          </button>
        </div>
        <div className="mt-2 text-xs text-gray-600">
          Use Stripe test card <strong>4242 4242 4242 4242</strong> (any valid future date / CVC) in test mode.
        </div>
      </div>
    </form>
  );
}

// ---------- Main component ----------
export default function PricingStripe({ userData, setActiveTab }) {
  const navigate = useNavigate();
  const [loadingStates, setLoadingStates] = useState({});
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState("");
  const [showStripeModal, setShowStripeModal] = useState(false);
  const [activeAmountEUR, setActiveAmountEUR] = useState(0);

  const auth = getAuth();
  const currentUser = auth.currentUser;
  const isPremiumUser = false;

  // billing state (minimal required fields for India export)
  const [billing, setBilling] = useState(() => {
    const u = getAuth().currentUser;
    return {
      name: u?.displayName || userData?.name || "",
      email: u?.email || userData?.email || "",
      line1: userData?.address_line1 || "",
      line2: userData?.address_line2 || "",
      city: userData?.city || "",
      state: userData?.state || "",
      postal_code: userData?.postal_code || "",
      country: (userData?.country || "").toUpperCase() || "",
      phone: userData?.phone || "",
    };
  });

  // NEW: indicate PaymentIntent creation in progress
  const [creatingIntent, setCreatingIntent] = useState(false);

  // helper: start Stripe checkout for given amount (creates PaymentIntent)
  const startStripeCheckout = async (amountEUR) => {
    try {
      setError(null);
      setClientSecret("");
      setCreatingIntent(true);

      // read fresh currentUser here to avoid stale capture
      const freshUser = getAuth().currentUser;
      if (!freshUser) {
        setError("Please sign in to purchase.");
        navigate("/auth");
        return;
      }

      // For non-INR presentment (EUR here) Indian regulations require buyer name + billing address.
      // Validate now (user has had a chance to fill the form)
      if (amountEUR > 0) {
        const name = (billing?.name || "").trim();
        const line1 = (billing?.line1 || "").trim();
        const country = (billing?.country || "").trim();
        if (!name || !line1 || !country || country.length !== 2) {
          setError("Please provide full billing name, address line1 and 2-letter country code (e.g. DE).");
          return;
        }
      }

      // Create PaymentIntent on backend (amount in cents)
      const cents = Math.round(amountEUR * 100);
      console.log("creating PaymentIntent with cents:", cents, "billing:", billing);
      const res = await axios.post(
        `${API_BASE_URL}/api/stripe/create-payment-intent`,
        {
          amount: cents,
          currency: "EUR",
          metadata: { uid: freshUser.uid, email: freshUser.email, name: freshUser.displayName },
          description: "Premium plan",
          uid: freshUser.uid,
          billing, // <-- pass billing info so backend can attach buyer name/address to PaymentIntent
        },
        { withCredentials: true, headers: { "Content-Type": "application/json" } }
      );

      const cs = res?.data?.data?.paymentIntent?.client_secret || res?.data?.paymentIntent?.client_secret;
      if (!cs) {
        console.error("missing client_secret in response:", res?.data);
        throw new Error("Missing client_secret from server");
      }

      setClientSecret(cs);
      setError(null);
    } catch (err) {
      console.error("create-payment-intent failed", err);
      setError(err?.response?.data?.error?.message || err?.message || "Failed to start payment.");
    } finally {
      setCreatingIntent(false);
    }
  };

  const handleFreePlan = async () => {
    try {
      const user = getAuth().currentUser;
      if (!user) {
        navigate("/auth");
        return;
      }

      // update Firestore directly if needed
      // (Your existing code used updateDoc; keep same behavior)
      // Here we just route to dashboard to keep parity — adapt as you like
      alert("Free plan activated (client-side demo).");
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      setError("Failed to activate Free plan.");
    }
  };

  // UI helpers
  const onPlanClick = (plan) => {
    if (plan.priceEUR === 0) {
      handleFreePlan();
      return;
    }

    if (!stripePromise) {
      setError("Stripe not configured (missing publishable key).");
      return;
    }

    // block if already premium
    if (isPremiumUser) return;

    setLoadingStates((p) => ({ ...p, [plan.name]: true }));
    setError(null);

    // Show modal with billing form first. Creating PaymentIntent is deferred until user clicks "Proceed to payment".
    setActiveAmountEUR(plan.priceEUR);
    setClientSecret(""); // clear any previous client secret
    setShowStripeModal(true);

    setLoadingStates((p) => ({ ...p, [plan.name]: false }));
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1A0637] via-[#2C0B4F] to-[#1A0637] text-white py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        {isPremiumUser && (
          <div className="mb-8 rounded-xl border border-[#FFD600]/30 bg-[#FFD600]/10 p-4 text-center">
            <span className="font-semibold text-[#FFD600]">You’re already on Premium.</span>{" "}
            <button
              onClick={() => (window.location.href = `${window.location.origin}/dashboard`)}
              className="ml-2 underline decoration-[#FFD600] underline-offset-4"
            >
              Go to Dashboard →
            </button>
          </div>
        )}

        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-lg text-gray-300">Find the perfect plan for your goals</p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {PLANS.map((plan) => {
            const isPremiumPlan = plan.name === "Premium Plan";
            const shouldDisable =
              loadingStates[plan.name] || (isPremiumUser && (isPremiumPlan || plan.priceEUR === 0));
            const ctaLabel =
              isPremiumUser && isPremiumPlan
                ? "You're Premium ✓"
                : isPremiumUser && plan.priceEUR === 0
                  ? "Included in Premium"
                  : loadingStates[plan.name]
                    ? "Processing..."
                    : plan.cta;

            const onClick = () => {
              if (shouldDisable) return;
              onPlanClick(plan);
            };

            const showRibbonText = isPremiumPlan && isPremiumUser ? "ALREADY PREMIUM" : plan.recommended ? "MOST POPULAR" : null;

            return (
              <div
                key={plan.name}
                className={`relative rounded-2xl p-8 transition-all duration-300 
                  ${plan.recommended
                    ? "bg-gradient-to-b from-[#3C0F6C] to-[#2C0B4F] border-2 border-[#FFD600] shadow-[#FFD600]/20"
                    : "bg-white/5 border border-white/10 hover:border-white/20"}`}
              >
                {showRibbonText && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span
                      className={`px-6 py-2 rounded-full text-sm font-bold shadow-lg
                      ${isPremiumPlan && isPremiumUser ? "bg-[#16a34a] text-white" : "bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6C]"}`}
                    >
                      {showRibbonText}
                    </span>
                  </div>
                )}

                <div className="text-center mb-8">
                  <h3 className="text-2xl font-bold mb-2">{plan.name}</h3>
                  <div className="text-4xl font-bold mb-2">
                    {plan.priceEUR === 0 ? "Free" : `€${plan.priceEUR.toFixed(2)}`}
                    {plan.priceEUR > 0 && <span className="text-lg text-gray-300"> one-time</span>}
                  </div>
                  {plan.priceEUR === 0 && <p className="text-sm text-gray-300 mb-4">No credit card required</p>}
                  {isPremiumPlan && isPremiumUser && <p className="text-sm text-gray-300 mb-2">Your subscription is active.</p>}
                </div>

                <ul className="space-y-4 mb-8 text-gray-200">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-center">
                      <svg className="h-5 w-5 text-[#FFD600] mr-3" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                        <path d="M5 13l4 4L19 7" />
                      </svg>
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <button
                  onClick={onClick}
                  disabled={shouldDisable}
                  className={`w-full py-3 px-6 rounded-xl font-semibold transition-all duration-300 
                    ${isPremiumPlan && isPremiumUser
                      ? "bg-white/10 border border-white/20 cursor-default"
                      : plan.recommended
                        ? "bg-gradient-to-r from-[#FFD600] to-[#FFA800] text-[#3C0F6F]"
                        : "bg-white/10 border border-white/20 hover:bg-white/20"}
                    ${shouldDisable ? "opacity-60 cursor-not-allowed" : ""}`}
                >
                  {ctaLabel}
                </button>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="mt-8 text-center text-red-400 bg-red-400/10 p-4 rounded-lg">
            {String(error)}
          </div>
        )}
      </div>

      {/* Stripe modal */}
      {showStripeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowStripeModal(false)} />
          <div className="relative w-full max-w-lg p-6">
            <div className="bg-white rounded-lg p-6 text-black shadow-xl">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Pay €{activeAmountEUR.toFixed(2)}</h3>
                <button onClick={() => setShowStripeModal(false)} className="text-gray-600">✕</button>
              </div>

              {/* Billing form required for export (EUR) */}
              <BillingAddressForm billing={billing} setBilling={setBilling} />

              {/* Proceed to create PaymentIntent (shows PaymentElement once clientSecret is received) */}
              {!clientSecret ? (
                <div className="flex gap-2 mb-4">
                  <button
                    type="button"                             // <- IMPORTANT: ensure it's not a submit
                    onClick={() => startStripeCheckout(activeAmountEUR)}
                    className="px-4 py-2 rounded-md bg-indigo-600 text-white"
                    disabled={creatingIntent}                  // <- disable while creating
                  >
                    {creatingIntent ? "Creating..." : "Proceed to payment"}
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowStripeModal(false)}
                    className="px-4 py-2 rounded-md border text-sm text-slate-700 bg-white"
                    disabled={creatingIntent}
                  >
                    Cancel
                  </button>
                </div>
              ) : null}

              {/* Only render Elements + PaymentElement when we have clientSecret */}
              {clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <StripeCheckoutForm
                    clientSecret={clientSecret}
                    amountEUR={activeAmountEUR}
                    onSuccess={() => {
                      setShowStripeModal(false);
                      window.location.reload(); // refresh profile to show premium
                    }}
                    onCancel={() => setShowStripeModal(false)}
                  />
                </Elements>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}